"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDetails = exports.useLabelDialogInitialState = void 0;
const tslib_1 = require("tslib");
const catalog_core_1 = require("@tableau/catalog-core");
const catalog_messages_1 = require("@tableau/catalog-messages");
const widgets_1 = require("@tableau/widgets");
const react_query_1 = require("@tanstack/react-query");
const react_1 = tslib_1.__importDefault(require("react"));
const LabelHelpers_1 = require("../LabelHelpers");
const labelCategories_1 = require("../restApi/labelCategories");
const Labels_1 = require("../restApi/Labels");
const labelValues_1 = require("../restApi/labelValues");
function labelIsRemoved(_a) {
    var { active, tombstone } = _a, label = tslib_1.__rest(_a, ["active", "tombstone"]);
    return !!label.luid && (tombstone || active === false);
}
function labelIsNewOrUpdated(_a, original) {
    var _b;
    var { active, elevated, message, tombstone } = _a, label = tslib_1.__rest(_a, ["active", "elevated", "message", "tombstone"]);
    if (!active || tombstone) {
        return false;
    }
    const existing = (_b = original === null || original === void 0 ? void 0 : original[label.category]) === null || _b === void 0 ? void 0 : _b[label.value];
    if (elevated !== (existing === null || existing === void 0 ? void 0 : existing.elevated)) {
        return true;
    }
    if (message !== (existing === null || existing === void 0 ? void 0 : existing.message)) {
        return true;
    }
    return false;
}
function useLabelDialogSubmit(contentItems, queryKey) {
    const deleteFn = (0, catalog_core_1.useRestFn)(Labels_1.Delete.deleteLabel);
    const putFn = (0, catalog_core_1.useRestFn)(Labels_1.Put.setLabels);
    const queryClient = (0, react_query_1.useQueryClient)();
    const mutateLabels = react_1.default.useCallback(({ state, original }) => tslib_1.__awaiter(this, void 0, void 0, function* () {
        const items = (0, catalog_core_1.filterOutNullAndUndefined)(Object.values(state)).flatMap(t => Object.values(t));
        const toDelete = (0, catalog_core_1.filterOutNullAndUndefined)(items.filter(labelIsRemoved).map(t => t.luid)).map((luid) => deleteFn({ delayOverride: 0, luid }));
        const toUpdate = items.filter(t => labelIsNewOrUpdated(t, original)).map(t => {
            var _a, _b;
            return putFn({
                contentItems,
                labelContent: {
                    active: (_a = t.active) !== null && _a !== void 0 ? _a : false,
                    message: (_b = t.message) !== null && _b !== void 0 ? _b : '',
                    value: t.value,
                    elevated: t.elevated === 'severe'
                        ? true
                        : false,
                },
                category: t.category,
                luid: t.luid,
            });
        });
        return yield Promise.all([...toDelete, ...toUpdate]);
    }), [contentItems, deleteFn, putFn]);
    const submit = (0, react_query_1.useMutation)({
        mutationFn: mutateLabels,
        onSettled: () => tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield queryClient.invalidateQueries({ queryKey });
        }),
        onError: () => widgets_1.ToastWidget.show({
            message: catalog_messages_1.Messages.LabelsDialog_SubmitFailed(),
            toastType: widgets_1.ToastType.Error,
            testId: 'submit-failed',
        }),
        onSuccess: (t) => {
            var _a, _b, _c;
            return widgets_1.ToastWidget.show({
                message: catalog_messages_1.Messages.LabelsDialog_SubmitSucceeded({
                    CONTENT_NAME: (_b = (_a = contentItems[0]) === null || _a === void 0 ? void 0 : _a.contentName) !== null && _b !== void 0 ? _b : catalog_messages_1.Messages.FieldUnknown(),
                    CONTENT_TYPE: (0, LabelHelpers_1.localizeLabelableContentType)((_c = contentItems[0]) === null || _c === void 0 ? void 0 : _c.contentType),
                    LABEL_COUNT: t.length,
                    LABEL_COUNT_LOC: catalog_messages_1.Formatters.formatCount(t.length),
                }),
                toastType: widgets_1.ToastType.Info,
                testId: 'submit-success',
            });
        },
    });
    return submit;
}
function useLabelDialogInitialState(contentItems, capabilities) {
    const categoriesFn = (0, catalog_core_1.useRestFnNoParams)(labelCategories_1.getLabelCategories);
    const valuesFn = (0, catalog_core_1.useRestFn)(labelValues_1.getLabelValues);
    const labelsFn = (0, catalog_core_1.useRestFn)(Labels_1.getLabelsSimple);
    const queryKey = react_1.default.useMemo(() => contentItems.map(t => t.contentId).sort((a, b) => a.localeCompare(b)), [
        contentItems,
    ]);
    const submit = useLabelDialogSubmit(contentItems, ['labels', ...queryKey]);
    const contentType = contentItems[0].contentType;
    const [valuesResp, categoriesResp, labelsResp] = (0, react_query_1.useQueries)({
        queries: [
            {
                refetchOnWindowFocus: false,
                queryKey: ['values', 'omitInternal'],
                queryFn: () => tslib_1.__awaiter(this, void 0, void 0, function* () {
                    return filterByCapability(filterAllowedCategories(contentType, yield valuesFn({ omitInternal: true })), capabilities);
                }),
            },
            {
                refetchOnWindowFocus: false,
                queryKey: ['categories', 'omitInternal'],
                queryFn: () => tslib_1.__awaiter(this, void 0, void 0, function* () { return filterByCapability(filterAllowedCategories(contentType, yield categoriesFn()), capabilities); }),
            },
            {
                refetchOnWindowFocus: false,
                queryKey: ['labels', ...queryKey],
                queryFn: () => labelsFn({ contentItems, transform: t => t }),
            },
        ],
    });
    const isLoading = valuesResp.isLoading || categoriesResp.isLoading || labelsResp.isLoading;
    const details = useDetails(isLoading, valuesResp.data, categoriesResp.data);
    const initialValues = useInitialValues(isLoading, valuesResp.data, labelsResp.data);
    return isLoading ? undefined : { details, initialValues, submit };
}
exports.useLabelDialogInitialState = useLabelDialogInitialState;
const empty = { categories: {}, values: {} };
function useDetails(isLoading, values, categories) {
    return react_1.default.useMemo(() => isLoading ? empty : toLabelDialogDetails(values !== null && values !== void 0 ? values : [], categories !== null && categories !== void 0 ? categories : []), [
        isLoading,
        values,
        categories,
    ]);
}
exports.useDetails = useDetails;
function useInitialValues(isLoading, values, labels) {
    return react_1.default.useMemo(() => isLoading ? {} : toInitialValues(values !== null && values !== void 0 ? values : [], labels !== null && labels !== void 0 ? labels : []), [
        isLoading,
        values,
        labels,
    ]);
}
function filterAllowedCategories(contentType, values) {
    if (contentType === 'flow' || contentType === 'column') {
        return values.filter(t => 'category' in t ? t.category.toLowerCase() !== 'certification' : t.name.toLowerCase() !== 'certification');
    }
    return values;
}
function filterByCapability(values, capabilities) {
    return values.filter(t => {
        const category = 'category' in t ? t.category.toLowerCase() : t.name.toLowerCase();
        if (category === 'certification') {
            return capabilities.certification;
        }
        else {
            return capabilities.warning;
        }
    });
}
function toLabelDialogDetails(valuesResp, categoriesResp) {
    const hasValues = new Set();
    const values = valuesResp.reduce((acc, { category, name, description, elevatedDefault }) => {
        hasValues.add(category);
        return Object.assign(Object.assign({}, acc), { [name]: { name, category, description, elevatedDefault } });
    }, {});
    const categories = categoriesResp.filter(t => hasValues.has(t.name)).reduce((acc, current) => (Object.assign(Object.assign({}, acc), { [current.name]: current })), {});
    return { values, categories };
}
function toInitialValues(valuesResp, labelsResp) {
    return valuesResp.reduce((acc, current) => {
        const existing = acc[current.category];
        const currentLabel = extractExistingLabelState(current, labelsResp);
        return Object.assign(Object.assign({}, acc), { [current.category]: Object.assign(Object.assign({}, existing), { [current.name]: currentLabel }) });
    }, {});
}
function extractExistingLabelState(current, labels) {
    const existing = labels.find(({ category, value }) => category === current.category && value === current.name);
    return existing
        ? {
            category: current.category,
            value: current.name,
            active: existing.active,
            elevated: existing.elevated === undefined ? undefined : (existing.elevated ? 'severe' : 'standard'),
            message: existing.message,
            tombstone: false,
            luid: existing.id,
        }
        : {
            tombstone: false,
            category: current.category,
            value: current.name,
        };
}
//# sourceMappingURL=useLabelDialogInitialState.js.map